<template>
  <div class="legal-search">
    <div class="legal-search-send">
      <LfLabelButtonComponent
        class="button"
        :text="texts.legalBtn"
        :title="texts.legalTooltip"
        :action="openSearchOnApp"
        :variant="LabelButtonVariant.OUTLINED"
      />
    </div>
    <v-tabs
      v-model="selectedTabIndex"
      background-color="transparent"
      hide-slider
      show-arrows
      :next-icon="icons.doubleArrowRight"
      :prev-icon="icons.doubleArrowLeft"
      class="tabs-other-styles legal-search-tabs"
      :ripple="false"
    >
      <v-tab
        v-for="(tab, tabIndex) in tabs"
        :key="`tab-${tab.area}`"
        :ripple="false"
        active-class="active"
        :class="[{ 'change-tab-styles': $vuetify.breakpoint.mdAndUp }, 'tab']"
        @change="onTabChange(tab)"
      >
        <span>{{ tab.title }}</span>
        <span v-if="tab.total">({{ tab.occurrences }})</span>
      </v-tab>
    </v-tabs>
    <v-tabs-items v-model="selectedCurrentTabIndex">
      <v-tab-item v-if="showContent" class="tab-content" :transition="false" :reverse-transition="false">
        <div>
          <div v-for="(item, itemIndex) in data" :key="itemIndex" class="legal-item">
            <div class="legal-separator">
              <div class="legal-lead-in">
                <span v-if="item.leadIn" v-html="item.leadIn"></span>
              </div>
              <div>
                <div class="legal-info-container">
                  <div
                    v-if="item.title"
                    v-html="item.title"
                    class="legal-title"
                    @click.stop="viewDocumentOnApp(item.nRef)"
                  ></div>
                  <LfLabelButtonComponent
                    class="button"
                    :text="'Ver en BBDD jurídica'"
                    :title="'Navegar en ventana nueva al documento original'"
                    :action="() => viewDocumentOnApp(item.nRef)"
                    :variant="LabelButtonVariant.TEXT"
                    :prependIcon="icons.database"
                  />
                </div>
                <div v-if="item.summary" class="legal-summary">
                  <img class="legal-icon" src="@/assets/images/svg/quote.svg" alt="Quote icon" />
                  <div class="legal-summary-text" v-html="item.summary"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="legal-footer">
          <PaginationComponent
            v-if="showContent && showPaginator"
            class="pagination-component"
            :style="cssVars"
            :totalPages="totalPages"
            :maxVisibleButtons="maxVisibleButtonsPage"
            :currentPage="numPage + 1"
            @pageChanged="onPageChanged"
          />
          <div class="select-wrapper">
            <div class="legal-select-num-recs">
              <v-select
                v-if="showPaginator"
                v-model="recsPerPage"
                :items="numRecsPerPage"
                :append-icon="icons.arrowDown"
                :menu-props="{ contentClass: 'legal-paginator-list' }"
                @change="refreshSearch"
              />
            </div>
            <div class="recs-per-page" v-if="showPaginator">
              {{ texts.recsPerPage }}
            </div>
          </div>
          <div class="legal-page-info" v-if="showPaginator">
            {{ numPagesLiteral }}
          </div>
        </div>
      </v-tab-item>
      <v-tab-item v-else>
        <SpinnerLayerComponent class="spinner-layer" />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import useRouter from '@/composables/useRouter'
import useLegalSearch from './composables/useLegalSearch'
import { useAuthStore } from '@/stores/auth/useAuthStore'
import { useI18n } from 'vue-i18n-composable'
import { Icons } from '@/icons/icons'
import { formatNumber } from '@/helpers/numeric'
import LfLabelButtonComponent from '@/components/forms/buttons/label/LfLabelButtonComponent.vue'
import { LabelButtonVariant } from '@/components/forms/buttons/types/ButtonTypes'
import PaginationComponent from '@/components/Pagination/PaginationComponent.vue'
import SpinnerLayerComponent from '@/components/Spinner/SpinnerLayerComponent.vue'
import { vars } from '@/styles/styleVars'
import { TabType } from './types/index'
import { LegalSearchDataType } from '@/store/modules/legalSearch/legalSearchTypes'

const { route } = useRouter()
const authStore = useAuthStore()
const { sessionId, searchTabs, searchData, loadSessionId, loadSearchTabs, loadSearchData } = useLegalSearch()

// TRANSLATIONS
const { t } = useI18n()
const texts = {
  legalBtn: t('components.legal_search.legal_btn').toString(),
  legalTooltip: t('components.legal_search.legal_tooltip').toString(),
  of: t('components.pagination.of').toString(),
  pages: t('components.pagination.pages').toString(),
  recsPerPage: t('components.pagination.records_per_page').toString(),
  registers: t('components.pagination.registers').toString()
}

// DATA
const icons = {
  database: Icons.DATABASE,
  doubleArrowLeft: Icons.DOUBLE_ARROW_LEFT,
  doubleArrowRight: Icons.DOUBLE_ARROW_RIGHT,
  arrowDown: Icons.ANGLE_DOWN
}
const numRecsPerPage = [5, 10, 20, 50]
const numPage = ref<number>(0)
const recsPerPage = ref(5)
const totalPages = ref(0)
const maxVisibleButtonsPage = ref(5)
const selectedTabIndex = ref(0)
const selectedCurrentTabIndex = ref(0)
const showContent = ref(true)
const currentTab = ref<TabType | null>(null)
const tabs = ref<TabType[]>(Array.isArray(searchTabs.value) ? searchTabs.value : [])
const data = Array.isArray(searchData.value) ? (searchData.value as LegalSearchDataType[]) : []
const searchTerm = computed(() => route?.params?.searchTerm ?? '')
const showPaginator = computed(() => currentTab.value && currentTab.value.occurrences > recsPerPage.value)
const numPagesLiteral = computed(() => {
  if (showPaginator.value && currentTab.value) {
    return `${numPage.value + 1} ${texts.of} ${totalPages.value} ${texts.pages} (${formatNumber(
      currentTab.value.occurrences
    )} ${texts.registers})`
  }
  return ''
})

// GETTERS
const encryptedUserId = computed<string>(() => authStore.encryptedUserId)

// METHODS
async function onTabChange(tab: TabType) {
  if (!currentTab.value || tab.area !== currentTab.value.area) {
    numPage.value = 0
  }
  currentTab.value = tab
  authStore.cancelPendingRequests()
  showContent.value = false
  await loadSearchData(searchTerm.value, sessionId.value, tab.area, numPage.value, recsPerPage.value)
  showContent.value = true
  totalPages.value = Math.ceil(currentTab.value.occurrences / recsPerPage.value)
}

function onPageChanged(page: number) {
  numPage.value = page - 1
  if (currentTab.value) {
    onTabChange(currentTab.value)
  }
}

function refreshSearch() {
  numPage.value = 0
  if (currentTab.value) {
    onTabChange(currentTab.value)
  }
}

function viewDocumentOnApp(nRef: string) {
  window.open(
    `${process.env.VUE_APP_LEGAL_SEARCH_OPEN_SERVICE}?ei=${encryptedUserId.value}&producto_inicial=UNIVERSAL&nref=${nRef}`,
    '_blank'
  )
}

function openSearchOnApp() {
  if (currentTab.value) {
    window.open(
      `${process.env.VUE_APP_LEGAL_SEARCH_OPEN_SERVICE}?ei=${encryptedUserId.value}&producto=UNIVERSAL&orden=relevancia&universal=${searchTerm.value}&subindices=${currentTab.value.area}`,
      '_blank'
    )
  }
}

const cssVars = computed(() => ({
  '--pagination-container-margin': 0,
  '--pagination-btn-active': vars.corporate_color,
  '--pagination-btn-active-border': vars.corporate_color,
  '--pagination-icon-color': vars.corporate_color,
  '--pagination-btn-not-active-border': vars.white_01,
  '--pagination-btn-color': vars.gray_01,
  '--pagination-btn-hover-color': vars.gray_01,
  '--pagination-icon-inactive-color': vars.disabled_color
}))

// HOOKS
onMounted(async () => {
  if (!tabs.value || tabs.value.length === 0) {
    await loadSessionId(encryptedUserId.value)
    await loadSearchTabs(searchTerm.value, sessionId.value)
  }
  if (tabs.value && tabs.value.length > 0) {
    await onTabChange(tabs.value[selectedTabIndex.value])
  }
})
</script>

<style lang="scss" scoped>
.legal-search {
  padding: $spacing-sm;
}

.legal-item {
  display: flex;
  border-bottom: 1px solid $main-400;
  padding: $spacing-sm;
  margin: $spacing-sm;

  .legal-separator {
    width: 100%;
  }

  &:hover {
    background-color: $neutral-grey-050;
  }
}

.legal-lead-in {
  span {
    display: inline-flex;
    font-size: 14px;
    background-color: $gray-04;
    color: $blue-14;
    padding: 6px 20px;
    margin: 0 20px 10px;
    ::v-deep .term {
      color: $red-09;
      display: contents;
    }
  }
}

.legal-info-container {
  display: flex;
  justify-content: space-between;
}

.legal-title {
  @include milano-bold-14;
  cursor: pointer;
  display: flex;
  color: $main-1000;
  padding-top: 5px;

  ::v-deep .term {
    color: $tertiary-persianRed-200;
    display: contents;
  }
}

.legal-summary {
  display: flex;
}

.legal-icon {
  width: 19px;
  height: 20px;
  margin-right: 10px;
}

.legal-summary-text {
  @include milano-regular-14;
  color: $neutral-grey-800;

  ::v-deep .term {
    color: $tertiary-persianRed-200;
    display: contents;
  }
}

.spinner-layer-container {
  height: 300px;
}

.legal-search-tabs {
  border-color: $main-1000 !important;

  .tab {
    @include milano-regular-16;
    color: $neutral-grey-800;
    text-transform: inherit;

    &:not(.active):hover {
      border-bottom: none !important;
    }

    &.active {
      border-color: $main-1000 !important;
    }
  }
}

.legal-search-send {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 50px;
  padding: 0px $spacing-xs 0px $cornerRadius-md;
  background-color: $neutral-grey-100;
}

.legal-footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 10px;
  position: relative;
  ::v-deep .pagination-component {
    display: flex;
    width: 300px;

    .pagination-item {
      button {
        width: auto;

        &.icon {
          font-size: 10px;
          width: auto;
          min-width: 24px;
        }
      }
    }
  }

  ::v-deep .legal-select-num-recs {
    display: flex;
    width: 70px;
    max-width: 70px;
    margin: 0;
    padding: 0;

    .v-select__selection {
      font-family: $secondary-font;
      font-size: 14px;
      color: $corporate-color;
      position: relative;
      top: -3px;
    }

    .v-text-field {
      margin: 0 20px 0 0;
      padding: 4px 0;
      height: 20px;
    }

    .lf-icon-angle-down {
      padding-bottom: 0px;
      top: 4px;
      position: relative;

      &::before {
        content: '';
        border-width: 8px;
        border-style: solid;
        border-right: 8px solid transparent;
        border-color: $corporate-color transparent transparent;
        width: 0;
        height: 0;
      }
    }

    .v-input__slot {
      height: 20px;
      &::before,
      &::after {
        border: none;
      }
    }
  }

  ::v-deep .legal-page-info {
    font-family: $corporate-font;
    font-size: 14px;
    color: $corporate-color;
    display: flex;
    align-self: end;
  }

  ::v-deep .recs-per-page {
    font-family: $corporate-font;
    font-size: 14px;
    color: $corporate-color;
    display: flex;
  }

  ::v-deep .select-wrapper {
    display: flex;
  }
}

::v-deep .v-list-item__content {
  padding: 0;
}
</style>

<style lang="scss">
.legal-paginator-list {
  border-radius: 0;
  border-top: 1px solid $corporate-color !important;
  box-shadow: none !important;
}
.v-menu__content.menuable__content__active .v-list-item {
  // max-height: 50px !important;
  height: 50px !important;
  padding-left: 16px !important;
  padding-right: 16px !important;
}

.v-menu__content.menuable__content__active .v-list-item.v-list-item--highlighted {
  background-color: $neutral-grey-100 !important;
}
</style>
